import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Alert,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import BillingIcon from '@mui/icons-material/CreditCard';
import ActiveIcon from '@mui/icons-material/CheckCircle';
import InactiveIcon from '@mui/icons-material/Cancel';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import LinkIcon from '@mui/icons-material/Link';
import CopyIcon from '@mui/icons-material/ContentCopy';
import { getApiErrorInfo } from '../../services/api';
import tenantService from '../../services/tenantService';
import userService from '../../services/userService';
import billingService from '../../services/billingService';
import type { Subscription, Plan } from '../../services/billingService';

interface TenantUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  createdAt: string;
  authProviderId?: string;
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 160, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

function StatusChip({ status }: { status: string }) {
  const isActive = status === 'active' || status === 'non_renewing';
  return (
    <Chip
      label={status}
      size="small"
      color={isActive ? 'success' : status === 'cancelled' ? 'error' : 'warning'}
      variant="outlined"
      sx={{ textTransform: 'capitalize' }}
    />
  );
}

export default function TenantDetail() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tenant, setTenant] = useState<any>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Invite user dialog
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', firstName: '', lastName: '', role: 'user' });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

  // Edit tenant dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editActive, setEditActive] = useState(true);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Checkout link generator
  const [plans, setPlans] = useState<Plan[]>([]);
  const [addons, setAddons] = useState<Plan[]>([]);
  const [checkoutPlanId, setCheckoutPlanId] = useState('');
  const [checkoutSeats, setCheckoutSeats] = useState(5);
  const [checkoutAddonIds, setCheckoutAddonIds] = useState<string[]>([]);
  const [checkoutAddonQtys, setCheckoutAddonQtys] = useState<Record<string, number>>({});
  const [checkoutLinkLoading, setCheckoutLinkLoading] = useState(false);
  const [checkoutLink, setCheckoutLink] = useState<string | null>(null);
  const [checkoutLinkError, setCheckoutLinkError] = useState<string | null>(null);
  const [checkoutLinkCopied, setCheckoutLinkCopied] = useState(false);

  useEffect(() => {
    if (!tenantId) return;
    loadAll(tenantId);
    loadPlansAndAddons();
  }, [tenantId]);

  async function loadAll(id: string) {
    setLoading(true);
    setError(null);
    try {
      const [tenantData, logoData, usersData] = await Promise.allSettled([
        tenantService.getTenantById(id),
        tenantService.getTenantLogo(id),
        userService.getAllUsers(id),
      ]);

      if (tenantData.status === 'fulfilled') setTenant(tenantData.value);
      else setError('Failed to load tenant.');

      if (logoData.status === 'fulfilled') setLogoUrl(logoData.value);
      if (usersData.status === 'fulfilled') setUsers(usersData.value ?? []);

      try {
        const sub = await billingService.getSubscription(id);
        setSubscription(sub);
      } catch {
        setSubscription(null);
      }
    } catch {
      setError('Failed to load tenant details.');
    } finally {
      setLoading(false);
    }
  }

  async function loadPlansAndAddons() {
    try {
      const [planList, addonList] = await Promise.all([
        billingService.getAvailablePlans(),
        billingService.getAvailableTenantAddons(),
      ]);
      setPlans(planList);
      setAddons(addonList);
      if (planList.length > 0 && !checkoutPlanId) {
        setCheckoutPlanId(planList[0].id);
      }
    } catch {
      // Non-critical
    }
  }

  const handleGenerateCheckoutLink = async () => {
    if (!tenantId || !checkoutPlanId) return;
    setCheckoutLinkLoading(true);
    setCheckoutLinkError(null);
    setCheckoutLink(null);
    try {
      const addonQtys: Record<string, number> = {};
      for (const id of checkoutAddonIds) {
        addonQtys[id] = checkoutAddonQtys[id] ?? checkoutSeats;
      }
      const page = await billingService.createAdminCheckoutLink(tenantId, {
        planId: checkoutPlanId,
        quantity: checkoutSeats,
        addonIds: checkoutAddonIds,
        addonQuantities: addonQtys,
      });
      setCheckoutLink(page.url);
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setCheckoutLinkError(apiError.message || 'Failed to generate checkout link.');
    } finally {
      setCheckoutLinkLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!checkoutLink) return;
    await navigator.clipboard.writeText(checkoutLink);
    setCheckoutLinkCopied(true);
    setTimeout(() => setCheckoutLinkCopied(false), 2000);
  };

  const toggleAddon = (addonId: string) => {
    setCheckoutAddonIds(prev =>
      prev.includes(addonId) ? prev.filter(id => id !== addonId) : [...prev, addonId]
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !tenant) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/app/system-admin/tenants')} sx={{ mb: 3 }}>
          All Tenants
        </Button>
        <Alert severity="error">{error ?? 'Tenant not found.'}</Alert>
      </Box>
    );
  }

  const handleOpenInvite = () => {
    setInviteForm({ email: '', firstName: '', lastName: '', role: 'user' });
    setInviteError(null);
    setInviteSuccess(null);
    setInviteOpen(true);
  };

  const handleInvite = async () => {
    const email = inviteForm.email.trim();
    if (!tenantId) return;
    if (!email) {
      setInviteError('Email is required.');
      return;
    }

    setInviteLoading(true);
    setInviteError(null);
    try {
      await userService.inviteUser(tenantId, { ...inviteForm, email });
      setInviteSuccess(`Invitation sent to ${email}.`);
      setTimeout(() => {
        loadAll(tenantId);
        setInviteOpen(false);
        setInviteSuccess(null);
      }, 1500);
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setInviteError(apiError.message || 'Failed to send invitation. Please try again.');
    } finally {
      setInviteLoading(false);
    }
  };

  const handleOpenEdit = () => {
    setEditName(tenant.name);
    setEditActive(tenant.isActive);
    setEditError(null);
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!tenantId) return;
    setEditLoading(true);
    setEditError(null);
    try {
      await tenantService.updateTenant(tenantId, { name: editName, isActive: editActive });
      setEditOpen(false);
      loadAll(tenantId);
    } catch (error: unknown) {
      const apiError = getApiErrorInfo(error);
      setEditError(apiError.message || 'Failed to update tenant.');
    } finally {
      setEditLoading(false);
    }
  };

  const addonLabels: Record<string, string> = {
    'OnDemand-Module-Yearly': 'On-Demand',
    'Webinars-Module-Yearly': 'Webinars',
    'Enterprise-OnDemand-Module-Yearly': 'Enterprise On-Demand',
    'Enterprise-Webinar-Module-Yearly': 'Enterprise Webinars',
  };

  return (
    <Box>
      {/* Back navigation */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/app/system-admin/tenants')}
        sx={{ mb: 3, color: 'text.secondary', fontWeight: 400 }}
      >
        All Tenants
      </Button>

      {/* Page header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          mb: 4,
          gap: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {logoUrl ? (
            <Box
              component="img"
              src={logoUrl}
              alt={`${tenant.name} logo`}
              sx={{
                height: 56,
                width: 'auto',
                objectFit: 'contain',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                p: 0.5,
              }}
            />
          ) : (
            <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
              <BusinessIcon />
            </Avatar>
          )}
          <Box>
            <Typography variant="h5" fontWeight={600}>
              {tenant.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
              <Chip
                icon={tenant.isActive ? <ActiveIcon fontSize="small" /> : <InactiveIcon fontSize="small" />}
                label={tenant.isActive ? 'Active' : 'Inactive'}
                size="small"
                color={tenant.isActive ? 'success' : 'error'}
                variant="outlined"
              />
              <Typography variant="caption" color="text.secondary">
                Since {new Date(tenant.createdAt).toLocaleDateString()}
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ fontFamily: 'monospace' }}
              >
                {tenant.id}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={() => navigate(`/app/system-admin/tenants/${tenantId}/import-credits`)}
          >
            Import credits
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleOpenEdit}
          >
            Edit Tenant
          </Button>
        </Box>
      </Box>

      {/* Content */}
      <Grid container spacing={3} alignItems="flex-start">
        {/* Left: Billing */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 3,
                py: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <BillingIcon fontSize="small" color="primary" />
              <Typography variant="subtitle1" fontWeight={500}>
                Subscription
              </Typography>
            </Box>

            {subscription ? (
              <Box sx={{ px: 3, py: 2 }}>
                <InfoRow label="Plan" value={subscription.planName || subscription.planId} />
                <InfoRow label="Status" value={<StatusChip status={subscription.status} />} />
                <InfoRow label="Billing Period" value={subscription.billingPeriod || '—'} />
                <InfoRow
                  label="Current Term"
                  value={
                    subscription.currentTermStart && subscription.currentTermEnd
                      ? `${new Date(subscription.currentTermStart).toLocaleDateString()} – ${new Date(subscription.currentTermEnd).toLocaleDateString()}`
                      : '—'
                  }
                />
                <InfoRow
                  label="Next Renewal"
                  value={
                    subscription.nextBillingAt
                      ? new Date(subscription.nextBillingAt).toLocaleDateString()
                      : '—'
                  }
                />
                <InfoRow
                  label="Max Users"
                  value={subscription.maxUsers > 0 ? subscription.maxUsers : 'Unlimited'}
                />
                {subscription.activeAddonIds?.length > 0 && (
                  <Box sx={{ py: 1.25 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Active Modules
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {subscription.activeAddonIds.map((id) => (
                        <Chip key={id} label={addonLabels[id] ?? id} size="small" color="primary" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
                {subscription.addonQuantities && Object.keys(subscription.addonQuantities).length > 0 && (
                  <Box sx={{ py: 1.25 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Seat Quantities
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {Object.entries(subscription.addonQuantities).map(([addonId, qty]) => (
                        <Chip
                          key={addonId}
                          label={`${addonLabels[addonId] ?? addonId}: ${qty}`}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              <Box sx={{ px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Alert severity="warning">
                  No active subscription. Generate a checkout link to send to the tenant admin.
                </Alert>

                {checkoutLinkError && (
                  <Alert severity="error" onClose={() => setCheckoutLinkError(null)}>
                    {checkoutLinkError}
                  </Alert>
                )}

                <FormControl fullWidth size="small">
                  <InputLabel>Base Plan</InputLabel>
                  <Select
                    label="Base Plan"
                    value={checkoutPlanId}
                    onChange={(e) => setCheckoutPlanId(e.target.value)}
                    disabled={checkoutLinkLoading}
                  >
                    {plans.length === 0 && (
                      <MenuItem value="" disabled>Loading plans…</MenuItem>
                    )}
                    {plans.map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.name || p.id}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Seat Count"
                  type="number"
                  size="small"
                  value={checkoutSeats}
                  onChange={(e) => setCheckoutSeats(Math.max(1, parseInt(e.target.value) || 1))}
                  disabled={checkoutLinkLoading}
                  InputProps={{ inputProps: { min: 1 } }}
                />

                {addons.length > 0 && (
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mb: 0.75, fontWeight: 500 }}
                    >
                      Add-ons
                    </Typography>
                    {addons.map((addon) => (
                      <Box key={addon.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={checkoutAddonIds.includes(addon.id)}
                              onChange={() => toggleAddon(addon.id)}
                              disabled={checkoutLinkLoading}
                            />
                          }
                          label={<Typography variant="body2">{addon.name || addon.id}</Typography>}
                          sx={{ flex: 1, mr: 0 }}
                        />
                        {checkoutAddonIds.includes(addon.id) && (
                          <TextField
                            size="small"
                            type="number"
                            label="Seats"
                            value={checkoutAddonQtys[addon.id] ?? checkoutSeats}
                            onChange={(e) =>
                              setCheckoutAddonQtys((prev) => ({
                                ...prev,
                                [addon.id]: Math.max(1, parseInt(e.target.value) || 1),
                              }))
                            }
                            disabled={checkoutLinkLoading}
                            sx={{ width: 90 }}
                            InputProps={{ inputProps: { min: 1 } }}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                )}

                <Button
                  variant="contained"
                  startIcon={
                    checkoutLinkLoading
                      ? <CircularProgress size={16} color="inherit" />
                      : <LinkIcon />
                  }
                  onClick={handleGenerateCheckoutLink}
                  disabled={!checkoutPlanId || checkoutLinkLoading}
                >
                  Generate Checkout Link
                </Button>

                {checkoutLink && (
                  <>
                    <Divider />
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 0.75, fontWeight: 500 }}
                      >
                        Checkout URL (expires in ~1 hour)
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={checkoutLink}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title={checkoutLinkCopied ? 'Copied!' : 'Copy to clipboard'}>
                                <IconButton size="small" onClick={handleCopyLink}>
                                  <CopyIcon fontSize="small" color={checkoutLinkCopied ? 'success' : 'inherit'} />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Email this link to the tenant admin to complete payment through Chargebee.
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Right: Users */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon fontSize="small" color="primary" />
                <Typography variant="subtitle1" fontWeight={500}>Users</Typography>
                <Chip label={users.length} size="small" />
              </Box>
              <Button
                size="small"
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={handleOpenInvite}
              >
                Invite User
              </Button>
            </Box>

            {users.length === 0 ? (
              <Box sx={{ py: 8, textAlign: 'center' }}>
                <PeopleIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">No users yet.</Typography>
                <Typography variant="body2" color="text.secondary">
                  Use Invite User to add members to this tenant.
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Joined</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'primary.light' }}>
                              {user.firstName?.[0]}{user.lastName?.[0]}
                            </Avatar>
                            <Box>
                              <Typography variant="body2">{user.firstName} {user.lastName}</Typography>
                              {!user.authProviderId && (
                                <Typography variant="caption" color="warning.main">Invite pending</Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>{user.email}</TableCell>
                        <TableCell>
                          <Chip
                            label={
                              user.role === 'tenant-admin' ? 'Tenant Admin'
                              : user.role === 'admin' ? 'System Admin'
                              : 'User'
                            }
                            size="small"
                            color={
                              user.role === 'tenant-admin' ? 'primary'
                              : user.role === 'admin' ? 'error'
                              : 'default'
                            }
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: 'text.secondary' }}>
                          {new Date(user.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell align="right">
                          {!user.authProviderId && tenantId && (
                            <Button
                              size="small"
                              variant="text"
                              onClick={async () => {
                                try {
                                  await userService.resendInvite(user.id, tenantId);
                                } catch { /* ignore */ }
                              }}
                            >
                              Resend
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Invite User Dialog */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Invite User to {tenant.name}
          <IconButton size="small" onClick={() => setInviteOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {inviteError && <Alert severity="error">{inviteError}</Alert>}
          {inviteSuccess && <Alert severity="success">{inviteSuccess}</Alert>}
          <TextField
            label="Email"
            type="email"
            required
            fullWidth
            value={inviteForm.email}
            onChange={(e) => setInviteForm(f => ({ ...f, email: e.target.value }))}
            disabled={inviteLoading || !!inviteSuccess}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="First Name"
              fullWidth
              value={inviteForm.firstName}
              onChange={(e) => setInviteForm(f => ({ ...f, firstName: e.target.value }))}
              disabled={inviteLoading || !!inviteSuccess}
            />
            <TextField
              label="Last Name"
              fullWidth
              value={inviteForm.lastName}
              onChange={(e) => setInviteForm(f => ({ ...f, lastName: e.target.value }))}
              disabled={inviteLoading || !!inviteSuccess}
            />
          </Box>
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              label="Role"
              value={inviteForm.role}
              onChange={(e) => setInviteForm(f => ({ ...f, role: e.target.value }))}
              disabled={inviteLoading || !!inviteSuccess}
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="tenant-admin">Tenant Admin</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setInviteOpen(false)} variant="outlined" disabled={inviteLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleInvite}
            disabled={!inviteForm.email.trim() || inviteLoading || !!inviteSuccess}
            startIcon={inviteLoading ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
          >
            Send Invitation
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Tenant Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Edit Tenant
          <IconButton size="small" onClick={() => setEditOpen(false)}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {editError && <Alert severity="error">{editError}</Alert>}
          <TextField
            label="Tenant Name"
            required
            fullWidth
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            disabled={editLoading}
          />
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={editActive ? 'active' : 'inactive'}
              onChange={(e) => setEditActive(e.target.value === 'active')}
              disabled={editLoading}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setEditOpen(false)} variant="outlined" disabled={editLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditSave}
            disabled={!editName || editLoading}
            startIcon={editLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
